import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import { Select, Input, Tag } from 'antd';

// Local Deps
import NFButton from '../../../../components/primitives/NFButton';
import msgs from '../../../../config/messages';

const { Option } = Select;

export default class OrgDescription extends React.PureComponent {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onMultiSelectOrganization: PropTypes.func.isRequired,
    organization: PropTypes.any.isRequired,
    industryAssociationsList: PropTypes.any.isRequired,
    naicsFoundryCodesList: PropTypes.any.isRequired,
    // naicsCastingCodesList: PropTypes.any.isRequired,
    naicsMachineCodesList: PropTypes.any.isRequired,
    naicsForgingCodesList: PropTypes.any.isRequired,
    naicsOthersCodesList: PropTypes.any.isRequired,
    industryTypesList: PropTypes.any.isRequired,
    reasonRegistration: PropTypes.string.isRequired,
    cage: PropTypes.string.isRequired,
  };

  // Custom tag renderer to prevent text truncation
  /* eslint-disable react/prop-types */
  customTagRender = (props) => {
    const { label, closable, onClose } = props;
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{
          whiteSpace: 'normal',
          height: 'auto',
          maxWidth: '100%',
          marginBottom: '4px',
          display: 'inline-block'
        }}
      >
        {label}
      </Tag>
    );
  };
  /* eslint-enable react/prop-types */

  render() {
    const {
      onSubmit,
      onCancel,
      onChange,
      onMultiSelectOrganization,
      organization,
      industryAssociationsList,
      naicsFoundryCodesList,
      // naicsCastingCodesList,
      naicsMachineCodesList,
      naicsForgingCodesList,
      naicsOthersCodesList,
      industryTypesList,
      reasonRegistration,
      cage,
    } = this.props;

    const industryTypesOptions = (industryTypesList || []).map(industryType => <Option value={industryType} label={industryType} key={industryType} id={industryType}>{industryType}</Option>);

    // Improved sort function to ensure numeric sorting
    const sortCodes = (codesList) => {
      // Add null/undefined check to prevent errors
      if (!codesList || typeof codesList !== 'object') {
        return [];
      }

      return Object.keys(codesList)
        .sort((a, b) => {
          // Split the codes into two parts: before and after the hyphen
          const [aPrefix, aSuffix] = a.split('-');
          const [bPrefix, bSuffix] = b.split('-');

          // Compare the main numeric part (prefix) first
          const prefixComparison = parseInt(aPrefix, 10) - parseInt(bPrefix, 10);

          // If the prefixes are the same, compare the suffix (after hyphen) as a number
          if (prefixComparison === 0) {
            return parseInt(aSuffix || '0', 10) - parseInt(bSuffix || '0', 10); // Default '0' if no suffix
          }

          return prefixComparison;
        })
        .map(code => <Option value={code} label={code} key={code}>{code} - {codesList[code]}</Option>);
    };

    // Map and sort NAIC codes for each industry type
    let foundryOptions = [];
    if (organization.industryType && organization.industryType.includes("Foundry")) {
      foundryOptions = sortCodes(naicsFoundryCodesList);
    }

    // let castingOptions = [];
    // if (organization.industryType && organization.industryType.includes("Casting")) {
    //   castingOptions = sortCodes(naicsCastingCodesList);
    // }

    let machineShopOptions = [];
    if (organization.industryType && organization.industryType.includes("Machine Shop")) {
      machineShopOptions = sortCodes(naicsMachineCodesList);
    }

    let forgingOptions = [];
    if (organization.industryType && organization.industryType.includes("Forging")) {
      forgingOptions = sortCodes(naicsForgingCodesList);
    }

    let otherNaicsOptions = [];
    if (organization.industryType && organization.industryType.includes("Other")) {
      otherNaicsOptions = sortCodes(naicsOthersCodesList);
    }

    // Debug logging to help identify the issue
    console.log('=== Industry Associations Debug ===');
    console.log('industryAssociationsList:', industryAssociationsList);
    console.log('industryAssociationsList type:', typeof industryAssociationsList);
    console.log('industryAssociationsList isArray:', Array.isArray(industryAssociationsList));
    console.log('industryAssociationsList length:', industryAssociationsList ? industryAssociationsList.length : 'null/undefined');

    if (industryAssociationsList && industryAssociationsList.length > 0) {
      console.log('First association item:', industryAssociationsList[0]);
      console.log('First association keys:', Object.keys(industryAssociationsList[0] || {}));
      console.log('All association items:', industryAssociationsList);
    } else {
      console.warn('industryAssociationsList is empty or null!');
    }

    // Safely map industry associations, filtering out any invalid entries
    // Handle different possible property name variations from Java serialization
    const rawList = industryAssociationsList || [];
    console.log('Processing rawList with length:', rawList.length);

    const validAssociations = rawList
      .map((association, index) => {
        console.log(`Processing association ${index}:`, association);
        if (!association || typeof association !== 'object') {
          console.log(`Association ${index} is invalid (not an object)`);
          return null;
        }

        // Check for various possible property name formats
        const sponsorAbrv = association.sponsorAbrv || association.sponsorabrv || association.SPONSOR_ABRV || association.sponsor_abrv;
        const sponsorName = association.sponsorName || association.sponsorname || association.SPONSOR_NAME || association.sponsor_name;

        console.log(`Association ${index} - sponsorAbrv:`, sponsorAbrv, 'sponsorName:', sponsorName);

        if (!sponsorAbrv || !sponsorName) {
          console.log(`Association ${index} is missing required fields`);
          return null;
        }

        // Normalize to expected format
        return {
          sponsorAbrv: String(sponsorAbrv).trim(),
          sponsorName: String(sponsorName).trim()
        };
      })
      .filter(item =>
        // Filter out null items AND filter out "None" entries from database
        // (we'll add "None" manually as a single option)
        item !== null &&
        item.sponsorAbrv &&
        item.sponsorName &&
        item.sponsorAbrv.toUpperCase() !== 'NONE'
      )
      // Remove duplicates based on sponsorAbrv
      .filter((item, index, self) =>
        index === self.findIndex(t => t.sponsorAbrv === item.sponsorAbrv)
      );

    console.log('Valid associations after processing (excluding None):', validAssociations.length, validAssociations);

    // Build options array - always include "None" as the first option, then add valid associations
    const industryAssociationOptions = [
      <Option value="None" label="None" key="industry-assoc-none">None - No Industry Association</Option>,
      ...validAssociations.map(association => (
        <Option
          value={association.sponsorAbrv}
          label={association.sponsorAbrv}
          key={`industry-assoc-${association.sponsorAbrv}`}
        >
          {association.sponsorAbrv} - {association.sponsorName}
        </Option>
      ))
    ];

    console.log('Final industryAssociationOptions count:', industryAssociationOptions.length);
    console.log('=== End Industry Associations Debug ===');

    return (
      <div>
        <Row style={{ flexWrap: 'nowrap' }}>
          <Col style={{ marginTop: '16px' }}>
            <div className="nf-registration__content">
              <Row className="nf-row--justify-end" />
              <div className="nf-registration__layout">
                <div>Industry Type (Select all that apply)*</div>
                <div className="nf-registration__input" id="industryTypesSelect">
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select all that apply"
                    onChange={newValue => {
                      onMultiSelectOrganization("industryType", newValue);
                    }}
                    optionLabelProp="label"
                    value={organization.industryType}
                    disabled={cage}
                    tagRender={this.customTagRender}
                  >
                    {industryTypesOptions}
                  </Select>
                </div>
                <div className="nf-registration__input">
                  <Input
                    onChange={onChange}
                    id="reasonRegistration"
                    name="reasonRegistration"
                    type="text"
                    placeholder="Reason for registering in ICON"
                    style={{ marginRight: '10px', marginLeft: '10px', display: "none" }}
                    value={reasonRegistration}
                  />
                </div>
                <div>NAICS Codes (Select all that apply)</div>
                <div className="nf-registration__input">
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select all that apply"
                    onChange={newValue => {
                      onMultiSelectOrganization("naics", newValue);
                    }}
                    optionLabelProp="label"
                    defaultValue={organization.naics}
                    disabled={cage}
                    tagRender={this.customTagRender}
                  >
                    {foundryOptions}
                    {/* {castingOptions} */}
                    {machineShopOptions}
                    {forgingOptions}
                    {otherNaicsOptions}
                    { }
                  </Select>
                </div>
                <div>Industry Association Memberships (Select all that apply)*</div>
                <div className="nf-registration__input">
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select all that apply"
                    onChange={newValue => {
                      onMultiSelectOrganization("industryAssociations", newValue);
                    }}
                    optionLabelProp="label"
                    value={organization.industryAssociations}
                    tagRender={this.customTagRender}
                  >
                    {industryAssociationOptions}
                  </Select>
                </div>
                <div className="nf-registration--wrapper">
                  <div className="nf-registration__buttons">
                    <NFButton
                      className="nf-button--gray-border"
                      style={{ marginRight: '16px' }}
                      onClick={() => {
                        onCancel("orgStep");
                      }}
                    >
                      {msgs.Form.backButton}
                    </NFButton>
                    <NFButton
                      className="nf-button--form nf-button--orange"
                      disabled={!organization.industryType || organization.industryType.length === 0 || !organization.industryAssociations || organization.industryAssociations.length === 0}
                      type="submit"
                      onClick={() => {
                        // Validate industry associations before submitting
                        if (!organization.industryAssociations || organization.industryAssociations.length === 0) {
                          alert('Please select at least one Industry Association or choose "None"');
                          return;
                        }
                        onSubmit();
                      }}
                    >
                      {msgs.Form.continueButton}
                    </NFButton>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/Registration/subComponents/OrgDescription/OrgDescription.jsx